const AUTH_KEYS = ["token", "userName", "userType", "adminType", "admin", "permissions"];

const hasValue = (value) => value != null && value !== "";

/** Mirror auth into localStorage so new tabs/windows keep the session. */
export const setAuthItem = (key, value) => {
  sessionStorage.setItem(key, value);
  localStorage.setItem(key, value);
};

export const getAuthItem = (key) => {
  const local = localStorage.getItem(key);
  if (hasValue(local)) return local;
  const session = sessionStorage.getItem(key);
  if (hasValue(session)) return session;
  return null;
};

export const isAuthenticated = () => Boolean(getAuthItem("token"));

/** Copy current-tab session auth into localStorage before opening a new tab. */
export const persistAuthForNewTab = () => {
  AUTH_KEYS.forEach((key) => {
    const sessionValue = sessionStorage.getItem(key);
    if (hasValue(sessionValue)) {
      localStorage.setItem(key, sessionValue);
    }
  });
};

/** Keep auth in sync across tabs (sessionStorage <-> localStorage). */
export const syncAuthFromLocalStorage = () => {
  AUTH_KEYS.forEach((key) => {
    const sessionValue = sessionStorage.getItem(key);
    const localValue = localStorage.getItem(key);
    if (hasValue(sessionValue) && !hasValue(localValue)) {
      localStorage.setItem(key, sessionValue);
    } else if (hasValue(localValue) && !hasValue(sessionValue)) {
      sessionStorage.setItem(key, localValue);
    }
  });
};

export const clearAuth = () => {
  AUTH_KEYS.forEach((key) => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });
};

export const getAuthSnapshot = () => {
  const auth = {};
  AUTH_KEYS.forEach((key) => {
    const value = getAuthItem(key);
    if (hasValue(value)) auth[key] = value;
  });
  return auth;
};

export const applyAuthSnapshot = (auth = {}) => {
  Object.entries(auth).forEach(([key, value]) => {
    if (hasValue(value)) setAuthItem(key, value);
  });
};

export const requestAuthFromOpener = () =>
  new Promise((resolve) => {
    if (isAuthenticated() || !window.opener || window.opener.closed) {
      resolve(isAuthenticated());
      return;
    }

    let settled = false;
    const finish = (ok) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      clearTimeout(timeoutId);
      resolve(ok);
    };

    const onMessage = (event) => {
      if (event.origin !== window.location.origin) return;
      if (event.data?.type !== "SUPPORT_CHAT_AUTH") return;
      applyAuthSnapshot(event.data.auth);
      finish(isAuthenticated());
    };

    const timeoutId = window.setTimeout(() => finish(isAuthenticated()), 1500);

    window.addEventListener("message", onMessage);
    window.opener.postMessage({ type: "SUPPORT_CHAT_REQUEST_AUTH" }, window.location.origin);
  });

export const installAuthOpenerListener = () => {
  const onMessage = (event) => {
    if (event.origin !== window.location.origin) return;
    if (event.data?.type !== "SUPPORT_CHAT_REQUEST_AUTH") return;
    event.source?.postMessage(
      { type: "SUPPORT_CHAT_AUTH", auth: getAuthSnapshot() },
      window.location.origin
    );
  };

  window.addEventListener("message", onMessage);
  return () => window.removeEventListener("message", onMessage);
};
